import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TreeControl } from '../shared/TreeControl';
import Splitter from '../shared/Splitter';
import { RefreshIcon } from '../../shared/icons';
import toast from 'react-hot-toast';
import apiClient from '../../shared/api/apiClient';
import { hasCapability } from '../../utils/accessControl';
import './styles.css';
import IntegrationCategories from './IntegrationCategories';
import CategoryAttributeSettings from './CategoryAttributeSettings';
import type {
    ClientCategoryDto,
    AssignedCategoryMappingDto,
    MappingsResponse,
    CategoryData,
    CategoriesProps,
    CreateCategoryRequest,
    MoveCategoryRequest,
    UpdateCategoryRequest,
    SubcategoryClearMapping,
    SubcategoryClearPreview
} from './types';
import type { TreeNode } from '../shared/TreeControl/types';

const convertCategoryToTreeNode = (category: ClientCategoryDto): TreeNode<CategoryData> => {
    return {
        id: category.id,
        name: category.name,
        children: [],
        data: {
            createdAt: category.createdAt,
            updatedAt: category.updatedAt,
            createdById: category.createdById,
            updatedById: category.updatedById,
            pricePercent: category.pricePercent,
            priceSum: category.priceSum,
            pricePercent2: category.pricePercent2,
            priceSum2: category.priceSum2
        },
        parentId: category.parentCategoryId
    };
};

const buildCategoryTree = (categories: ClientCategoryDto[]): TreeNode<CategoryData>[] => {
    const categoryMap = new Map<number, TreeNode<CategoryData>>();
    const rootCategories: TreeNode<CategoryData>[] = [];

    categories.forEach(cat => {
        const treeNode = convertCategoryToTreeNode(cat);
        categoryMap.set(cat.id, treeNode);
    });

    categories.forEach(cat => {
        const category = categoryMap.get(cat.id)!;
        if (cat.parentCategoryId) {
            const parent = categoryMap.get(cat.parentCategoryId);
            if (parent) {
                parent.children.push(category);
            }
        } else {
            rootCategories.push(category);
        }
    });

    return rootCategories;
};

type PriceDraft = {
    pricePercent: string;
    priceSum: string;
    pricePercent2: string;
    priceSum2: string;
};

type PriceField = keyof PriceDraft;

type PriceNumber = 1 | 2;

type EditingPrice = {
    supplierCategoryId: number;
    priceNumber: PriceNumber;
};

type RightPanelMode = 'mappings' | 'attributes' | 'category';

const CategoryManagement: React.FC<CategoriesProps> = ({ currentUser }) => {
    const [categories, setCategories] = useState<TreeNode<CategoryData>[]>([]);
    const [loading, setLoading] = useState(true);
    const [leftPanelWidth, setLeftPanelWidth] = useState(300);
    const [isResizing, setIsResizing] = useState(false);
    const [topPanelHeight, setTopPanelHeight] = useState(() => {
        const saved = localStorage.getItem('categoryManagement_topPanelHeight');
        return saved ? parseInt(saved, 10) : 280;
    });
    const [isResizingVertical, setIsResizingVertical] = useState(false);
    const [assignedCategories, setAssignedCategories] = useState<AssignedCategoryMappingDto[]>([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState<number | undefined>(undefined);
    const [selectedSupplierCategories, setSelectedSupplierCategories] = useState<Set<number>>(new Set());
    const [searchQuery, setSearchQuery] = useState('');
    const [effectiveSearchQuery, setEffectiveSearchQuery] = useState('');
    const [savingSingleMapping, setSavingSingleMapping] = useState(false);
    const [hideEmptyCategories, setHideEmptyCategories] = useState(() => {
        const saved = localStorage.getItem('categoryManagement_hideEmptyCategories');
        return saved ? JSON.parse(saved) : false;
    });
    const [hideCheckedCategories, setHideCheckedCategories] = useState(() => {
        const saved = localStorage.getItem('categoryManagement_hideCheckedCategories');
        return saved ? JSON.parse(saved) : false;
    });
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
    const [rightPanelMode, setRightPanelMode] = useState<RightPanelMode>('mappings');
    const [categoryNameDraft, setCategoryNameDraft] = useState('');
    const [savingCategoryDetails, setSavingCategoryDetails] = useState(false);
    const [movingCategory, setMovingCategory] = useState<TreeNode<CategoryData> | null>(null);
    const [targetParentId, setTargetParentId] = useState<number | undefined>(undefined);
    const [savingMove, setSavingMove] = useState(false);
    const [clearPreview, setClearPreview] = useState<{ parentId: number; mappings: SubcategoryClearMapping[]; productCount: number } | null>(null);
    const [clearSubmitting, setClearSubmitting] = useState(false);
    const [categoryTreeRevision, setCategoryTreeRevision] = useState(0);
    const canEdit = hasCapability(currentUser, 'categories.edit');
    const containerRef = useRef<HTMLDivElement>(null);

    const [rootId, setRootId] = useState<number | undefined>(undefined);
    const assignedCategoriesRef = useRef<AssignedCategoryMappingDto[]>([]);
    const immediateMappingLoadRef = useRef<number | undefined>(undefined);
    const mappingRequestSequenceRef = useRef(0);

    useEffect(() => {
        assignedCategoriesRef.current = assignedCategories;
    }, [assignedCategories]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setEffectiveSearchQuery(searchQuery);
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [searchQuery]);

    // Save filter states to localStorage
    useEffect(() => {
        localStorage.setItem('categoryManagement_hideEmptyCategories', JSON.stringify(hideEmptyCategories));
    }, [hideEmptyCategories]);

    useEffect(() => {
        localStorage.setItem('categoryManagement_hideCheckedCategories', JSON.stringify(hideCheckedCategories));
    }, [hideCheckedCategories]);

    useEffect(() => {
        localStorage.setItem('categoryManagement_topPanelHeight', topPanelHeight.toString());
    }, [topPanelHeight]);

    const loadCategories = useCallback(async () => {
        try {
            setLoading(true);

            const response = await apiClient.get<ClientCategoryDto[]>(
                'api/client-categories');

            const treeData = buildCategoryTree(response.data || []);

            setRootId(treeData.length ? treeData[0].id : undefined);
            setCategories(treeData);

        } catch (error) {
            console.error('Ошибка при загрузке категорий:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadAssignedCategories = useCallback(async (categoryId: number) => {
        immediateMappingLoadRef.current = categoryId;
        const requestSequence = ++mappingRequestSequenceRef.current;

        if (!categoryId || categoryId === rootId || categoryId < 0) {
            setAssignedCategories([]);
            React.startTransition(() => setSelectedSupplierCategories(new Set()));
            return;
        }

        try {
            const response = await apiClient.get<MappingsResponse>(
                `api/client-categories/${categoryId}/mappings`);

            if (requestSequence !== mappingRequestSequenceRef.current) {
                return;
            }

            const mappings = response.data.mappings;

            mappings.sort((a, b) => {

                if (a.rating < b.rating) return -1;
                if (a.rating > b.rating) return 1;

                // Then by supplier name and category name
                const labelA = `${a.integrationName} > ${a.supplierCategoryName}`;
                const labelB = `${b.integrationName} > ${b.supplierCategoryName}`;
                return labelA.localeCompare(labelB);
            });

            setAssignedCategories(mappings);
            React.startTransition(() => {
                setSelectedSupplierCategories(new Set(mappings.map(m => m.supplierCategoryId)));
            });
        } catch (error) {
            if (requestSequence !== mappingRequestSequenceRef.current) {
                return;
            }

            console.error('Error loading assigned categories:', error);
            setAssignedCategories([]);
            React.startTransition(() => setSelectedSupplierCategories(new Set()));
        }
    }, [rootId]);

    const setSelectedCategory = useCallback((node: TreeNode | undefined) => {
        const nextCategoryId = node?.id;
        if (nextCategoryId !== undefined) {
            void loadAssignedCategories(nextCategoryId);
        } else {
            setAssignedCategories([]);
            React.startTransition(() => setSelectedSupplierCategories(new Set()));
        }

        setSelectedCategoryId(nextCategoryId);
    }, [loadAssignedCategories]);

    const findCategoryNode = useCallback((nodes: TreeNode<CategoryData>[], categoryId?: number): TreeNode<CategoryData> | undefined => {
        if (!categoryId) {
            return undefined;
        }

        for (const node of nodes) {
            if (node.id === categoryId) {
                return node;
            }

            const found = findCategoryNode(node.children, categoryId);
            if (found) {
                return found;
            }
        }

        return undefined;
    }, []);

    const handleSupplierCategorySelect = useCallback(async (categoryId: number) => {
        if (!selectedCategoryId || selectedCategoryId === rootId) {
            toast.error('Сначала выберите категорию');
            return;
        }

        if (savingSingleMapping || loading) {
            return;
        }

        const targetNode = findCategoryNode(categories, selectedCategoryId);
        if (targetNode && targetNode.children.length > 0) {
            toast.error('Нельзя привязывать прайс к категории с подкатегориями. Выберите конечную категорию.');
            return;
        }

        try {
            setSavingSingleMapping(true);

            // Update local state first - add the new category
            setSelectedSupplierCategories(prev => {
                const newSet = new Set(prev); // Start with current selections
                newSet.add(categoryId); // Add the new one
                return newSet;
            });

            const rating = assignedCategoriesRef.current.reduce((p, c) => p < c.rating ? c.rating : p, 0) + 1;

            // Immediately save the new mapping using the new single endpoint
            await apiClient.post(
                `api/client-categories/${selectedCategoryId}/mappings/${categoryId}?rating=${rating}`,
                {});

            // Refresh assigned categories to show the new mapping
            await loadAssignedCategories(selectedCategoryId);

        } catch (error) {
            console.error('Error saving mapping:', error);
            // Revert selection if save failed
            setSelectedSupplierCategories(prev => {
                const newSet = new Set(prev);
                newSet.delete(categoryId);
                return newSet;
            });
        } finally {
            setSavingSingleMapping(false);
        }
    }, [selectedCategoryId, loadAssignedCategories, savingSingleMapping, loading, rootId, categories, findCategoryNode]);

    const handleSupplierCategoryDeselect = useCallback(async (categoryId: number) => {
        if (!selectedCategoryId || selectedCategoryId === rootId) {
            toast.error('Сначала выберите категорию');
            return;
        }

        if (savingSingleMapping || loading) {
            return;
        }

        try {
            setSavingSingleMapping(true);

            // Update local state first
            setSelectedSupplierCategories(prev => {
                const newSet = new Set(prev);
                newSet.delete(categoryId);
                return newSet;
            });

            // Immediately delete the mapping using DELETE endpoint
            await apiClient.delete(
                `api/client-categories/${selectedCategoryId}/mappings/${categoryId}`);

            // Refresh assigned categories to show the updated list
            await loadAssignedCategories(selectedCategoryId);

        } catch (error) {
            console.error('Error deleting mapping:', error);
            // Revert selection if delete failed
            setSelectedSupplierCategories(prev => new Set([...prev, categoryId]));
        } finally {
            setSavingSingleMapping(false);
        }
    }, [selectedCategoryId, loadAssignedCategories, savingSingleMapping, loading, rootId]);

    const handleRemoveAssignedCategory = useCallback(async (categoryId: number) => {
        if (!selectedCategoryId || selectedCategoryId === rootId || savingSingleMapping) {
            return;
        }

        try {
            setSavingSingleMapping(true);

            await apiClient.delete(
                `api/client-categories/${selectedCategoryId}/mappings/${categoryId}`);

            // Remove from local state immediately after successful deletion
            setSelectedSupplierCategories(prev => {
                const newSet = new Set(prev);
                newSet.delete(categoryId);
                return newSet;
            });

            toast.success('Маппинг удален');
        } catch (error) {
            console.error('Error removing assigned category:', error);
        } finally {
            setSavingSingleMapping(false);

            // Refresh assigned categories to show the updated list
            await loadAssignedCategories(selectedCategoryId);
        }
    }, [selectedCategoryId, loadAssignedCategories, savingSingleMapping, rootId]);

    const [priceDrafts, setPriceDrafts] = useState<Record<number, PriceDraft>>({});
    const [editingPrice, setEditingPrice] = useState<EditingPrice | null>(null);
    const [editingCategoryPrice, setEditingCategoryPrice] = useState<PriceNumber | null>(null);
    const [savingPrice, setSavingPrice] = useState(false);
    const [categoryPriceDraft, setCategoryPriceDraft] = useState<PriceDraft>({
        pricePercent: '',
        priceSum: '',
        pricePercent2: '',
        priceSum2: ''
    });
    const [savingCategoryPrice, setSavingCategoryPrice] = useState(false);

    const getPriceValue = useCallback((value?: number) => value?.toString() || '', []);

    const selectedCategoryNode = React.useMemo(
        () => findCategoryNode(categories, selectedCategoryId),
        [categories, findCategoryNode, selectedCategoryId]
    );
    const selectedSupplierCategoryIds = React.useMemo(
        () => Array.from(selectedSupplierCategories),
        [selectedSupplierCategories]
    );
    const categoryNameChanged = selectedCategoryNode !== undefined
        && categoryNameDraft.trim() !== selectedCategoryNode.name;

    useEffect(() => {
        setCategoryNameDraft(selectedCategoryNode?.name ?? '');
    }, [selectedCategoryNode]);

    const buildAvailableParentTree = useCallback((nodes: TreeNode<CategoryData>[], excludedCategoryId: number): TreeNode<CategoryData>[] => {
        return nodes
            .filter(node => node.id !== excludedCategoryId)
            .map(node => ({
                ...node,
                children: buildAvailableParentTree(node.children, excludedCategoryId)
            }));
    }, []);

    const findCategoryPathIds = useCallback((nodes: TreeNode<CategoryData>[], categoryId: number, path: number[] = []): number[] => {
        for (const node of nodes) {
            const nextPath = [...path, node.id];
            if (node.id === categoryId) {
                return nextPath;
            }

            const childPath = findCategoryPathIds(node.children, categoryId, nextPath);
            if (childPath.length > 0) {
                return childPath;
            }
        }

        return [];
    }, []);

    const expandCategoryPath = useCallback((categoryId: number) => {
        const expandedStorageKey = 'tree-control-expanded-categories';
        let storedExpandedIds: unknown[] = [];

        try {
            const parsedExpandedIds = JSON.parse(localStorage.getItem(expandedStorageKey) ?? '[]');
            storedExpandedIds = Array.isArray(parsedExpandedIds) ? parsedExpandedIds : [];
        } catch {
            storedExpandedIds = [];
        }

        const expandedIds = new Set(storedExpandedIds.map(id => Number(id)).filter(id => !Number.isNaN(id)));
        const pathIds = findCategoryPathIds(categories, categoryId);
        const idsToExpand = pathIds.length > 0 ? pathIds : [categoryId];

        idsToExpand.forEach(id => expandedIds.add(id));
        localStorage.setItem(expandedStorageKey, JSON.stringify(Array.from(expandedIds)));
        setCategoryTreeRevision(prev => prev + 1);
    }, [categories, findCategoryPathIds]);

    const handleSaveCategoryDetails = useCallback(async () => {
        if (!selectedCategoryNode || selectedCategoryNode.id === rootId || !categoryNameChanged) {
            return;
        }

        const name = categoryNameDraft.trim();
        if (!name) {
            toast.error('Введите название категории');
            return;
        }

        const requestData: UpdateCategoryRequest = { name };

        try {
            setSavingCategoryDetails(true);
            await apiClient.put(`api/client-categories/${selectedCategoryNode.id}`, requestData);
            toast.success('Категория обновлена');
            await loadCategories();
        } catch (error) {
            console.error('Error saving category:', error);
            toast.error('Ошибка при сохранении категории');
        } finally {
            setSavingCategoryDetails(false);
        }
    }, [categoryNameChanged, categoryNameDraft, loadCategories, rootId, selectedCategoryNode]);

    const handleResetCategoryDetails = useCallback(() => {
        setCategoryNameDraft(selectedCategoryNode?.name ?? '');
    }, [selectedCategoryNode]);

    const createSubcategory = useCallback(async (parentId: number, clearExisting: boolean) => {
        const requestData: CreateCategoryRequest = {
            name: 'Новая категория',
            parentCategoryId: parentId,
            clearExisting
        };
        const apiResponse = await apiClient.post<ClientCategoryDto>('api/client-categories', requestData);
        expandCategoryPath(parentId);
        await loadCategories();
        setSelectedCategoryId(apiResponse.data.id);
        setRightPanelMode('category');
        toast.success('Категория добавлена');
    }, [expandCategoryPath, loadCategories]);

    const handleAddChildCategory = useCallback(async () => {
        if (!selectedCategoryNode || savingCategoryDetails) {
            return;
        }

        const parentId = selectedCategoryNode.id;

        try {
            setSavingCategoryDetails(true);
            // Pre-check: если у родителя есть привязки/НЛ — показать диалог очистки,
            // иначе создать подкатегорию сразу.
            const preview = await apiClient.get<SubcategoryClearPreview>(
                `api/client-categories/${parentId}/subcategory-clear-preview`);
            const mappings = preview.data.mappings ?? [];
            const productCount = preview.data.productCount ?? 0;

            if (mappings.length > 0 || productCount > 0) {
                setClearPreview({ parentId, mappings, productCount });
                return;
            }

            await createSubcategory(parentId, false);
        } catch (error) {
            console.error('Error adding category:', error);
            toast.error('Ошибка при добавлении категории');
        } finally {
            setSavingCategoryDetails(false);
        }
    }, [createSubcategory, savingCategoryDetails, selectedCategoryNode]);

    const handleCancelClearPreview = useCallback(() => {
        if (clearSubmitting) {
            return;
        }
        setClearPreview(null);
    }, [clearSubmitting]);

    const handleConfirmClearAndCreate = useCallback(async () => {
        if (!clearPreview || clearSubmitting) {
            return;
        }

        try {
            setClearSubmitting(true);
            await createSubcategory(clearPreview.parentId, true);
            setClearPreview(null);
        } catch (error) {
            console.error('Error adding category after clear:', error);
            toast.error('Ошибка при добавлении категории');
        } finally {
            setClearSubmitting(false);
        }
    }, [clearPreview, clearSubmitting, createSubcategory]);

    const handleDeleteCategory = useCallback(async () => {
        if (!selectedCategoryNode || selectedCategoryNode.id === rootId || savingCategoryDetails) {
            return;
        }

        if (!window.confirm('Вы уверены, что хотите удалить эту категорию?')) {
            return;
        }

        try {
            setSavingCategoryDetails(true);
            await apiClient.delete(`api/client-categories/${selectedCategoryNode.id}`);
            toast.success('Категория удалена');
            setSelectedCategoryId(selectedCategoryNode.parentId);
            await loadCategories();
        } catch (error) {
            console.error('Error deleting category:', error);
            toast.error('Ошибка при удалении категории');
        } finally {
            setSavingCategoryDetails(false);
        }
    }, [loadCategories, rootId, savingCategoryDetails, selectedCategoryNode]);

    const handleOpenMoveCategory = useCallback(() => {
        if (!selectedCategoryNode || selectedCategoryNode.id === rootId) {
            return;
        }

        setMovingCategory(selectedCategoryNode);
        setTargetParentId(selectedCategoryNode.parentId);
    }, [rootId, selectedCategoryNode]);

    const handleCloseMoveCategory = useCallback(() => {
        if (savingMove) {
            return;
        }

        setMovingCategory(null);
        setTargetParentId(undefined);
    }, [savingMove]);

    const handleSaveMoveCategory = useCallback(async () => {
        if (!movingCategory || !targetParentId) {
            return;
        }

        const requestData: MoveCategoryRequest = {
            parentCategoryId: targetParentId
        };

        try {
            setSavingMove(true);
            await apiClient.put(`api/client-categories/${movingCategory.id}/parent`, requestData);
            expandCategoryPath(targetParentId);
            await loadCategories();
            setSelectedCategoryId(movingCategory.id);
            setMovingCategory(null);
            setTargetParentId(undefined);
            toast.success('Категория перемещена');
        } catch (error) {
            console.error('Error moving category:', error);
            toast.error('Ошибка при перемещении категории');
        } finally {
            setSavingMove(false);
        }
    }, [expandCategoryPath, loadCategories, movingCategory, targetParentId]);

    useEffect(() => {
        setPriceDrafts(
            assignedCategories.reduce<Record<number, PriceDraft>>((acc, mapping) => {
                acc[mapping.supplierCategoryId] = {
                    pricePercent: getPriceValue(mapping.pricePercent),
                    priceSum: getPriceValue(mapping.priceSum),
                    pricePercent2: getPriceValue(mapping.pricePercent2),
                    priceSum2: getPriceValue(mapping.priceSum2)
                };
                return acc;
            }, {})
        );
    }, [assignedCategories, getPriceValue]);

    useEffect(() => {
        setCategoryPriceDraft({
            pricePercent: getPriceValue(selectedCategoryNode?.data?.pricePercent),
            priceSum: getPriceValue(selectedCategoryNode?.data?.priceSum),
            pricePercent2: getPriceValue(selectedCategoryNode?.data?.pricePercent2),
            priceSum2: getPriceValue(selectedCategoryNode?.data?.priceSum2)
        });
    }, [selectedCategoryNode, getPriceValue]);

    const handlePriceDraftChange = useCallback((
        supplierCategoryId: number,
        field: PriceField,
        value: string
    ) => {
        setPriceDrafts(prev => ({
            ...prev,
            [supplierCategoryId]: {
                pricePercent: prev[supplierCategoryId]?.pricePercent ?? '',
                priceSum: prev[supplierCategoryId]?.priceSum ?? '',
                pricePercent2: prev[supplierCategoryId]?.pricePercent2 ?? '',
                priceSum2: prev[supplierCategoryId]?.priceSum2 ?? '',
                [field]: value
            }
        }));
    }, []);

    const handleCategoryPriceDraftChange = useCallback((field: PriceField, value: string) => {
        setCategoryPriceDraft(prev => ({
            ...prev,
            [field]: value
        }));
    }, []);

    const handleStartPriceEdit = useCallback((mapping: AssignedCategoryMappingDto, priceNumber: PriceNumber) => {
        setEditingPrice({ supplierCategoryId: mapping.supplierCategoryId, priceNumber });
        setPriceDrafts(prev => ({
            ...prev,
            [mapping.supplierCategoryId]: {
                pricePercent: prev[mapping.supplierCategoryId]?.pricePercent ?? getPriceValue(mapping.pricePercent),
                priceSum: prev[mapping.supplierCategoryId]?.priceSum ?? getPriceValue(mapping.priceSum),
                pricePercent2: prev[mapping.supplierCategoryId]?.pricePercent2 ?? getPriceValue(mapping.pricePercent2),
                priceSum2: prev[mapping.supplierCategoryId]?.priceSum2 ?? getPriceValue(mapping.priceSum2)
            }
        }));
    }, [getPriceValue]);

    const handleSavePrice = useCallback(async (supplierCategoryId: number) => {
        if (!selectedCategoryId) return;
        try {
            setSavingPrice(true);
            const draft = priceDrafts[supplierCategoryId] ?? { pricePercent: '', priceSum: '', pricePercent2: '', priceSum2: '' };
            const pricePercent = draft.pricePercent ? parseFloat(draft.pricePercent) : null;
            const priceSum = draft.priceSum ? parseFloat(draft.priceSum) : null;
            const pricePercent2 = draft.pricePercent2 ? parseFloat(draft.pricePercent2) : null;
            const priceSum2 = draft.priceSum2 ? parseFloat(draft.priceSum2) : null;
            await apiClient.post(
                `api/client-categories/${selectedCategoryId}/mappings/${supplierCategoryId}/price`,
                { pricePercent, priceSum, pricePercent2, priceSum2 });
            toast.success('Наценка обновлена');
            setEditingPrice(null);
            await loadAssignedCategories(selectedCategoryId);
        } catch (error) {
            console.error('Error saving price:', error);
            toast.error('Ошибка при сохранении наценки');
        } finally {
            setSavingPrice(false);
        }
    }, [selectedCategoryId, priceDrafts, loadAssignedCategories]);

    const handleSaveCategoryPrice = useCallback(async () => {
        if (!selectedCategoryId || selectedCategoryId === rootId) return;

        try {
            setSavingCategoryPrice(true);
            const pricePercent = categoryPriceDraft.pricePercent ? parseFloat(categoryPriceDraft.pricePercent) : null;
            const priceSum = categoryPriceDraft.priceSum ? parseFloat(categoryPriceDraft.priceSum) : null;
            const pricePercent2 = categoryPriceDraft.pricePercent2 ? parseFloat(categoryPriceDraft.pricePercent2) : null;
            const priceSum2 = categoryPriceDraft.priceSum2 ? parseFloat(categoryPriceDraft.priceSum2) : null;

            await apiClient.post(
                `api/client-categories/${selectedCategoryId}/price`,
                { pricePercent, priceSum, pricePercent2, priceSum2 });

            toast.success('Наценка категории обновлена');
            setEditingCategoryPrice(null);
            await loadCategories();
        } catch (error) {
            console.error('Error saving category price:', error);
            toast.error('Ошибка при сохранении наценки категории');
        } finally {
            setSavingCategoryPrice(false);
        }
    }, [categoryPriceDraft, loadCategories, rootId, selectedCategoryId]);

    const [dragItemIndex, setDragItemIndex] = useState<number | null>(null);
    const handleDragStart = useCallback((e: React.DragEvent<HTMLDivElement>, index: number) => {
        setDragItemIndex(index);
        e.dataTransfer.setData('text/plain', index.toString());
        e.dataTransfer.effectAllowed = 'move';
    }, []);

    const handleDragOver = useCallback((e: React.DragEvent<HTMLLIElement>) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const index = parseInt(e.currentTarget.getAttribute('data-index') || '');
        if (!isNaN(index)) {
            setDragOverIndex(index);
        }
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent<HTMLLIElement>) => {
        e.preventDefault();
        setDragOverIndex(null);
    }, []);

    const handleDragEnd = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragOverIndex(null);
        setDragItemIndex(null);
    }, []);

    const handleDrop = useCallback(async (e: React.DragEvent<HTMLLIElement>, dropIndex: number) => {
        e.preventDefault();
        if (!selectedCategoryId || selectedCategoryId === rootId) {
            toast.error('Сначала выберите категорию');
            return;
        }

        const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
        if (isNaN(dragIndex) || dragIndex === dropIndex) return;

        // Create a new array with the reordered items
        const newMappings = [...assignedCategories];
        const [draggedItem] = newMappings.splice(dragIndex, 1);
        newMappings.splice(dropIndex, 0, draggedItem);

        // Update local state immediately
        setAssignedCategories(newMappings);

        const updatedMappings = newMappings.map((mapping, index) => {
            const newRating = index + 1;
            return { ...mapping, rating: newRating };
        });

        // Save the new ratings to the backend in a single request
        try {
            const ratingUpdates = updatedMappings.map(mapping => ({
                supplierCategoryId: mapping.supplierCategoryId,
                rating: mapping.rating
            }));

            await apiClient.post(
                `api/client-categories/${selectedCategoryId}/mappings/ratings`,
                { ratingUpdates });

            // Refresh from server to ensure consistency
            await loadAssignedCategories(selectedCategoryId);
            toast.success('Порядок категорий обновлен');
        } catch (error) {
            console.error('Error updating category ratings:', error);
            // Revert to original order on error
            await loadAssignedCategories(selectedCategoryId);
            toast.error('Ошибка при обновлении порядка категорий');
        }
    }, [selectedCategoryId, assignedCategories, rootId, loadAssignedCategories]);

    useEffect(() => {
        if (categories.length > 0 && selectedCategoryId) {
            if (immediateMappingLoadRef.current === selectedCategoryId) {
                immediateMappingLoadRef.current = undefined;
                return;
            }

            loadAssignedCategories(selectedCategoryId);
        } else {
            React.startTransition(() => setSelectedSupplierCategories(new Set()));
        }
    }, [categories, selectedCategoryId, loadAssignedCategories]);

    useEffect(() => {
        loadCategories();
    }, [loadCategories]);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setIsResizing(true);
    }, []);

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!isResizing || !containerRef.current) return;

        const newWidth = e.clientX - containerRef.current.getBoundingClientRect().left;
        // Constrain width between min and max values
        const constrainedWidth = Math.max(200, Math.min(600, newWidth));
        setLeftPanelWidth(constrainedWidth);
    }, [isResizing]);

    const handleMouseUp = useCallback(() => {
        setIsResizing(false);
    }, []);

    const handleMouseDownVertical = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setIsResizingVertical(true);
    }, []);

    const handleMouseMoveVertical = useCallback((e: MouseEvent) => {
        if (!isResizingVertical || !containerRef.current) return;

        const rightPanelContent = containerRef.current.querySelector('.right-panel-content') as HTMLElement;
        if (!rightPanelContent) return;

        const rect = rightPanelContent.getBoundingClientRect();
        const minPanelHeight = 140;
        const splitterHeight = 6;
        const newHeight = e.clientY - rect.top;
        const maxTopHeight = Math.max(minPanelHeight, rect.height - splitterHeight - minPanelHeight);
        const constrainedHeight = Math.max(minPanelHeight, Math.min(maxTopHeight, newHeight));
        setTopPanelHeight(constrainedHeight);
    }, [isResizingVertical]);

    const handleMouseUpVertical = useCallback(() => {
        setIsResizingVertical(false);
    }, []);

    // Add event listeners for resizing
    useEffect(() => {
        if (isResizing) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        } else {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing, handleMouseMove, handleMouseUp]);

    useEffect(() => {
        if (isResizingVertical) {
            window.addEventListener('mousemove', handleMouseMoveVertical);
            window.addEventListener('mouseup', handleMouseUpVertical);
        } else {
            window.removeEventListener('mousemove', handleMouseMoveVertical);
            window.removeEventListener('mouseup', handleMouseUpVertical);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMoveVertical);
            window.removeEventListener('mouseup', handleMouseUpVertical);
        };
    }, [isResizingVertical, handleMouseMoveVertical, handleMouseUpVertical]);

    useEffect(() => {
        const adjustTopPanelHeight = () => {
            if (!containerRef.current) return;

            const rightPanelContent = containerRef.current.querySelector('.right-panel-content') as HTMLElement;
            if (!rightPanelContent) return;

            const minPanelHeight = 140;
            const splitterHeight = 6;
            const maxTopHeight = Math.max(minPanelHeight, rightPanelContent.clientHeight - splitterHeight - minPanelHeight);

            if (topPanelHeight > maxTopHeight) {
                setTopPanelHeight(maxTopHeight);
            }
        };

        adjustTopPanelHeight();
        window.addEventListener('resize', adjustTopPanelHeight);
        return () => window.removeEventListener('resize', adjustTopPanelHeight);
    }, [topPanelHeight, categories.length]);

    const handleReconfigureProducts = useCallback(async () => {
        await apiClient.post(
                `api/client-categories/${selectedCategoryId}/reconfigure`,
                {});
    }, [selectedCategoryId]);

    // Функция для получения полного пути клиентской категории по ID (без корневой категории)
    const getClientCategoryPath = useCallback((clientCategoryId: number): string => {
        const findCategoryPath = (nodes: TreeNode<CategoryData>[], path: string[] = []): string | null => {
            for (const node of nodes) {
                if (node.id === clientCategoryId) {
                    // Пропускаем первый элемент пути (корневую категорию)
                    const fullPath = [...path, node.name];
                    return fullPath.length > 1 ? fullPath.slice(1).join(' > ') : fullPath[0];
                }
                if (node.children && node.children.length > 0) {
                    const found = findCategoryPath(node.children, [...path, node.name]);
                    if (found) return found;
                }
            }
            return null;
        };

        const categoryPath = findCategoryPath(categories);
        return categoryPath || `${clientCategoryId}`;
    }, [categories]);

    const hasPriceChanges = useCallback((mapping: AssignedCategoryMappingDto, priceNumber: PriceNumber) => {
        const draft = priceDrafts[mapping.supplierCategoryId];
        if (!draft) {
            return false;
        }

        if (priceNumber === 1) {
            return draft.pricePercent !== getPriceValue(mapping.pricePercent)
                || draft.priceSum !== getPriceValue(mapping.priceSum);
        }

        return draft.pricePercent2 !== getPriceValue(mapping.pricePercent2)
            || draft.priceSum2 !== getPriceValue(mapping.priceSum2);
    }, [priceDrafts, getPriceValue]);

    const hasCategoryPriceChanges = useCallback((priceNumber?: PriceNumber) => {
        if (!selectedCategoryNode) {
            return false;
        }

        if (priceNumber === 1) {
            return categoryPriceDraft.pricePercent !== getPriceValue(selectedCategoryNode.data?.pricePercent)
                || categoryPriceDraft.priceSum !== getPriceValue(selectedCategoryNode.data?.priceSum);
        }

        if (priceNumber === 2) {
            return categoryPriceDraft.pricePercent2 !== getPriceValue(selectedCategoryNode.data?.pricePercent2)
                || categoryPriceDraft.priceSum2 !== getPriceValue(selectedCategoryNode.data?.priceSum2);
        }

        return categoryPriceDraft.pricePercent !== getPriceValue(selectedCategoryNode.data?.pricePercent)
            || categoryPriceDraft.priceSum !== getPriceValue(selectedCategoryNode.data?.priceSum)
            || categoryPriceDraft.pricePercent2 !== getPriceValue(selectedCategoryNode.data?.pricePercent2)
            || categoryPriceDraft.priceSum2 !== getPriceValue(selectedCategoryNode.data?.priceSum2);
    }, [categoryPriceDraft, getPriceValue, selectedCategoryNode]);

    const getPriceDisplayText = useCallback((pricePercent?: number, priceSum?: number) => {
        const parts: string[] = [];

        if (pricePercent !== undefined && pricePercent !== null && pricePercent !== 0) {
            parts.push(`${pricePercent}%`);
        }

        if (priceSum !== undefined && priceSum !== null && priceSum !== 0) {
            parts.push(`${parts.length > 0 && priceSum > 0 ? '+ ' : ''}${priceSum}р`);
        }

        return parts.length > 0 ? parts.join(' ') : null;
    }, []);

    const handleCancelCategoryPriceEdit = useCallback(() => {
        setCategoryPriceDraft({
            pricePercent: getPriceValue(selectedCategoryNode?.data?.pricePercent),
            priceSum: getPriceValue(selectedCategoryNode?.data?.priceSum),
            pricePercent2: getPriceValue(selectedCategoryNode?.data?.pricePercent2),
            priceSum2: getPriceValue(selectedCategoryNode?.data?.priceSum2)
        });
        setEditingCategoryPrice(null);
    }, [getPriceValue, selectedCategoryNode]);

    const handleCategoryPriceEditorKeyDown = useCallback((
        e: React.KeyboardEvent<HTMLInputElement>,
        priceNumber: PriceNumber
    ) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (hasCategoryPriceChanges(priceNumber)) {
                void handleSaveCategoryPrice();
            }
        }

        if (e.key === 'Escape') {
            e.preventDefault();
            handleCancelCategoryPriceEdit();
        }
    }, [handleCancelCategoryPriceEdit, handleSaveCategoryPrice, hasCategoryPriceChanges]);

    const handleCategoryPriceEditorBlur = useCallback((
        e: React.FocusEvent<HTMLDivElement>,
        priceNumber: PriceNumber
    ) => {
        const nextFocusedElement = e.relatedTarget as Node | null;
        if (nextFocusedElement && e.currentTarget.contains(nextFocusedElement)) {
            return;
        }

        if (hasCategoryPriceChanges(priceNumber)) {
            void handleSaveCategoryPrice();
            return;
        }

        handleCancelCategoryPriceEdit();
    }, [handleCancelCategoryPriceEdit, handleSaveCategoryPrice, hasCategoryPriceChanges]);

    const renderCategoryPriceCell = (priceNumber: PriceNumber) => {
        const isFirstPrice = priceNumber === 1;
        const isEditing = editingCategoryPrice === priceNumber;
        const pricePercentField: PriceField = isFirstPrice ? 'pricePercent' : 'pricePercent2';
        const priceSumField: PriceField = isFirstPrice ? 'priceSum' : 'priceSum2';
        const pricePercent = isFirstPrice
            ? selectedCategoryNode?.data?.pricePercent
            : selectedCategoryNode?.data?.pricePercent2;
        const priceSum = isFirstPrice
            ? selectedCategoryNode?.data?.priceSum
            : selectedCategoryNode?.data?.priceSum2;
        const displayText = getPriceDisplayText(pricePercent, priceSum);
        const disabled = !canEdit || !selectedCategoryId || selectedCategoryId === rootId || savingCategoryPrice;

        return (
            <div className="price-cell">
                {isEditing ? (
                    <div
                        className="price-editor"
                        onBlur={(e) => handleCategoryPriceEditorBlur(e, priceNumber)}
                    >
                        <input
                            type="number"
                            className="form-control form-control-sm price-editor-input"
                            placeholder="%"
                            value={categoryPriceDraft[pricePercentField]}
                            onChange={(e) => handleCategoryPriceDraftChange(pricePercentField, e.target.value)}
                            onKeyDown={(e) => handleCategoryPriceEditorKeyDown(e, priceNumber)}
                            disabled={savingCategoryPrice}
                            autoFocus
                        />
                        <input
                            type="number"
                            className="form-control form-control-sm price-editor-input"
                            placeholder="р"
                            value={categoryPriceDraft[priceSumField]}
                            onChange={(e) => handleCategoryPriceDraftChange(priceSumField, e.target.value)}
                            onKeyDown={(e) => handleCategoryPriceEditorKeyDown(e, priceNumber)}
                            disabled={savingCategoryPrice}
                        />
                        <button
                            className="btn btn-sm btn-primary price-save-button"
                            onClick={handleSaveCategoryPrice}
                            disabled={savingCategoryPrice || !hasCategoryPriceChanges(priceNumber)}
                            title="Сохранить наценку категории"
                            type="button"
                        >
                            {savingCategoryPrice ? '...' : '✓'}
                        </button>
                    </div>
                ) : (
                    <button
                        className={`price-display ${displayText ? '' : 'is-empty'}`}
                        onClick={() => setEditingCategoryPrice(priceNumber)}
                        disabled={disabled}
                        title="Редактировать наценку категории"
                        type="button"
                    >
                        {displayText ?? 'Наценка'}
                    </button>
                )}
            </div>
        );
    };

    const handleCancelPriceEdit = useCallback((mapping: AssignedCategoryMappingDto) => {
        setPriceDrafts(prev => ({
            ...prev,
            [mapping.supplierCategoryId]: {
                pricePercent: getPriceValue(mapping.pricePercent),
                priceSum: getPriceValue(mapping.priceSum),
                pricePercent2: getPriceValue(mapping.pricePercent2),
                priceSum2: getPriceValue(mapping.priceSum2)
            }
        }));
        setEditingPrice(null);
    }, [getPriceValue]);

    const handlePriceEditorKeyDown = useCallback((
        e: React.KeyboardEvent<HTMLInputElement>,
        mapping: AssignedCategoryMappingDto,
        priceNumber: PriceNumber
    ) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (hasPriceChanges(mapping, priceNumber)) {
                void handleSavePrice(mapping.supplierCategoryId);
            }
        }

        if (e.key === 'Escape') {
            e.preventDefault();
            handleCancelPriceEdit(mapping);
        }
    }, [handleCancelPriceEdit, handleSavePrice, hasPriceChanges]);

    const handlePriceEditorBlur = useCallback((
        e: React.FocusEvent<HTMLDivElement>,
        mapping: AssignedCategoryMappingDto,
        priceNumber: PriceNumber
    ) => {
        const nextFocusedElement = e.relatedTarget as Node | null;
        if (nextFocusedElement && e.currentTarget.contains(nextFocusedElement)) {
            return;
        }

        if (hasPriceChanges(mapping, priceNumber)) {
            void handleSavePrice(mapping.supplierCategoryId);
            return;
        }

        handleCancelPriceEdit(mapping);
    }, [handleCancelPriceEdit, handleSavePrice, hasPriceChanges]);

    const renderPriceCell = (mapping: AssignedCategoryMappingDto, priceNumber: PriceNumber) => {
        const isFirstPrice = priceNumber === 1;
        const isEditing = editingPrice?.supplierCategoryId === mapping.supplierCategoryId
            && editingPrice.priceNumber === priceNumber;
        const pricePercentField: PriceField = isFirstPrice ? 'pricePercent' : 'pricePercent2';
        const priceSumField: PriceField = isFirstPrice ? 'priceSum' : 'priceSum2';
        const pricePercent = isFirstPrice ? mapping.pricePercent : mapping.pricePercent2;
        const priceSum = isFirstPrice ? mapping.priceSum : mapping.priceSum2;
        const displayText = getPriceDisplayText(pricePercent, priceSum);

        return (
            <div className="price-cell">
                {isEditing ? (
                    <div
                        className="price-editor"
                        onBlur={(e) => handlePriceEditorBlur(e, mapping, priceNumber)}
                    >
                        <input
                            type="number"
                            className="form-control form-control-sm price-editor-input"
                            placeholder="%"
                            value={priceDrafts[mapping.supplierCategoryId]?.[pricePercentField] ?? ''}
                            onChange={(e) => handlePriceDraftChange(mapping.supplierCategoryId, pricePercentField, e.target.value)}
                            onKeyDown={(e) => handlePriceEditorKeyDown(e, mapping, priceNumber)}
                            disabled={savingPrice}
                            autoFocus
                        />
                        <input
                            type="number"
                            className="form-control form-control-sm price-editor-input"
                            placeholder="р"
                            value={priceDrafts[mapping.supplierCategoryId]?.[priceSumField] ?? ''}
                            onChange={(e) => handlePriceDraftChange(mapping.supplierCategoryId, priceSumField, e.target.value)}
                            onKeyDown={(e) => handlePriceEditorKeyDown(e, mapping, priceNumber)}
                            disabled={savingPrice}
                        />
                        <button
                            className="btn btn-sm btn-primary price-save-button"
                            onClick={() => handleSavePrice(mapping.supplierCategoryId)}
                            disabled={savingPrice || !hasPriceChanges(mapping, priceNumber)}
                            title="Сохранить наценку"
                            type="button"
                        >
                            {savingPrice ? '...' : '✓'}
                        </button>
                    </div>
                ) : (
                    <button
                        className={`price-display ${displayText ? '' : 'is-empty'}`}
                        onClick={() => handleStartPriceEdit(mapping, priceNumber)}
                        title="Редактировать наценку"
                        type="button"
                    >
                        {displayText ?? 'Наценка'}
                    </button>
                )}
            </div>
        );
    };
    const movingParentTree = React.useMemo(
        () => movingCategory ? buildAvailableParentTree(categories, movingCategory.id) : [],
        [buildAvailableParentTree, categories, movingCategory]
    );

    return (
        <>
        <div className="category-management container-fluid" ref={containerRef}>
            <div className="filter-controls-compact">
                <div className="filter-controls-row">
                    <h3>Привязка категорий</h3>

                    <div className="filter-controls-right">
                        <button className="btn-compact refresh" onClick={loadCategories} disabled={loading} title="Обновить">
                            {loading ? (
                                <div className="btn-spinner" />
                            ) : (
                                <RefreshIcon />
                            )}
                        </button>
                    </div>
                </div>
            </div>

            <div className="new-categories-container">
                <div className="split-view">
                    <div className="left-panel" style={{ width: leftPanelWidth }}>
                        <div className="tree-container">
                            <TreeControl
                                key={`categories-${categoryTreeRevision}`}
                                data={categories}
                                selectedNodeId={selectedCategoryId}
                                treeId={`categories`}
                                allowExpansion={true}
                                onNodeSelected={setSelectedCategory}
                                className="category-tree-control"
                                showChildCount={false}
                            />
                        </div>
                    </div>

                    <Splitter
                        direction="horizontal"
                        onMouseDown={handleMouseDown}
                        isResizing={isResizing}
                    />

                    <div className="right-panel">
                        <div className="right-panel-content">
                            <div className="category-panel-tabs">
                                <button
                                    className={rightPanelMode === 'mappings' ? 'active' : ''}
                                    onClick={() => setRightPanelMode('mappings')}
                                    type="button"
                                >
                                    Категории поставщиков
                                </button>
                                <button
                                    className={rightPanelMode === 'attributes' ? 'active' : ''}
                                    onClick={() => setRightPanelMode('attributes')}
                                    type="button"
                                >
                                    Атрибуты
                                </button>
                                <button
                                    className={rightPanelMode === 'category' ? 'active' : ''}
                                    onClick={() => setRightPanelMode('category')}
                                    type="button"
                                >
                                    Категория
                                </button>
                            </div>

                            {rightPanelMode === 'mappings' ? (
                                <>
                            <div
                                className="assigned-categories-section"
                                style={{ height: topPanelHeight, flex: '0 0 auto', minHeight: 140 }}
                            >
                                <div className="section-header">
                                    <h3 className="section-title">Назначенные категории</h3>
                                    {selectedCategoryId && selectedCategoryId !== rootId ? (
                                        <div className="category-price-toolbar">
                                            {renderCategoryPriceCell(1)}
                                            {renderCategoryPriceCell(2)}
                                            <button
                                                className="btn btn-sm btn-primary category-reconfigure-button"
                                                onClick={handleReconfigureProducts}
                                                title="Обновить конфигурацию продуктов"
                                                type="button"
                                            >
                                                ✅
                                            </button>
                                        </div>
                                    ) : null}
                                </div>

                                {assignedCategories.length > 0 ? (
                                    <ul className="category-list">
                                        {assignedCategories.map((mapping, index) => (
                                            <React.Fragment key={mapping.supplierCategoryId}>
                                                {dragItemIndex !== null && dragOverIndex === index && dragItemIndex > dragOverIndex && (
                                                    <div className="drop-indicator" />
                                                )}
                                                <li
                                                    key={mapping.supplierCategoryId}
                                                    className="category-item"
                                                    data-index={index}
                                                    title={mapping.fullPath || `${mapping.integrationName} > ${mapping.supplierCategoryName}`}
                                                    onDragOver={handleDragOver}
                                                    onDragLeave={handleDragLeave}
                                                    onDrop={(e) => handleDrop(e, index)}
                                                >
                                                    <div
                                                        className="drag-handle"
                                                        draggable={canEdit}
                                                        onDragStart={(e) => handleDragStart(e, index)}
                                                        onDragEnd={handleDragEnd}
                                                        title="Перетащите для изменения порядка"
                                                    >
                                                        ☰
                                                    </div>
                                                    <div className="category-item-content">
                                                        <span className="category-item-title">
                                                            {mapping.integrationName} &gt; {mapping.fullPath}
                                                        </span>
                                                    </div>
                                                    <div className="category-item-actions">
                                                        <div className="price-actions">
                                                            {renderPriceCell(mapping, 1)}
                                                            {renderPriceCell(mapping, 2)}
                                                        </div>
                                                        <button
                                                            className="btn-remove-category"
                                                            onClick={() => handleRemoveAssignedCategory(mapping.supplierCategoryId)}
                                                            title="Отвязать категорию"
                                                        >
                                                            ✕
                                                        </button>
                                                    </div>
                                                </li>
                                                {dragItemIndex !== null && dragOverIndex === index && dragItemIndex < dragOverIndex && (
                                                    <div className="drop-indicator" />
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </ul>
                                ) : (
                                    <div className="content-placeholder">
                                        <p>Нет назначенных категорий</p>
                                    </div>
                                )}
                            </div>

                            <Splitter
                                direction="vertical"
                                onMouseDown={handleMouseDownVertical}
                                isResizing={isResizingVertical}
                            />

                            <div className="integration-categories-section">
                                <div className="section-header">
                                    <input
                                        type="text"
                                        placeholder="Поиск..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="form-control"
                                        style={{ flex: 1 }}
                                    />
                                    <button
                                        className={`btn btn-sm ${hideEmptyCategories ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        onClick={() => setHideEmptyCategories(!hideEmptyCategories)}
                                        title="Показать/скрыть категории без продуктов"
                                    >
                                        📦
                                    </button>
                                    <button
                                        className={`btn btn-sm ${hideCheckedCategories ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        onClick={() => setHideCheckedCategories(!hideCheckedCategories)}
                                        title="Показать/скрыть выбранные категории"
                                    >
                                        ✓
                                    </button>
                                </div>
                                <div className='integration-categories-container'>
                                    <IntegrationCategories
                                        currentUser={currentUser}
                                        selectionMode={true}
                                        selectedCategoryIds={selectedSupplierCategoryIds}
                                        onCategorySelect={handleSupplierCategorySelect}
                                        onCategoryDeselect={handleSupplierCategoryDeselect}
                                        searchQuery={effectiveSearchQuery.trim()}
                                        hideEmptyCategories={hideEmptyCategories}
                                        hideCheckedCategories={hideCheckedCategories}
                                        getClientCategoryName={getClientCategoryPath}
                                        selectedClientCategoryId={selectedCategoryId}
                                    />
                                </div>
                            </div>
                                </>
                            ) : rightPanelMode === 'attributes' ? (
                                <CategoryAttributeSettings
                                    categoryId={selectedCategoryId}
                                    rootCategoryId={rootId}
                                    canEdit={canEdit}
                                />
                            ) : (
                                <div className="category-details-section">
                                    {!selectedCategoryNode ? (
                                        <div className="content-placeholder">
                                            <p>Выберите категорию в дереве</p>
                                        </div>
                                    ) : (
                                        <div className="category-inspector">
                                            <div className="category-inspector-section">
                                                <div className="category-inspector-section-title">Название</div>
                                                <div className="category-details-row">
                                                    <input
                                                        id="category-name"
                                                        type="text"
                                                        className="form-control category-name-input"
                                                        value={categoryNameDraft}
                                                        onChange={(e) => setCategoryNameDraft(e.target.value)}
                                                        disabled={!canEdit || selectedCategoryNode.id === rootId || savingCategoryDetails}
                                                    />
                                                </div>

                                                <div className="category-details-actions category-save-actions">
                                                    <button
                                                        className="btn-primary-custom category-action-button"
                                                        onClick={handleSaveCategoryDetails}
                                                        disabled={!canEdit || selectedCategoryNode.id === rootId || !categoryNameChanged || savingCategoryDetails}
                                                        type="button"
                                                    >
                                                        {savingCategoryDetails ? 'Сохранение...' : 'Сохранить'}
                                                    </button>
                                                    <button
                                                        className="btn-secondary-custom category-action-button"
                                                        onClick={handleResetCategoryDetails}
                                                        disabled={!categoryNameChanged || savingCategoryDetails}
                                                        type="button"
                                                    >
                                                        Отмена
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="category-inspector-section">
                                                <div className="category-inspector-section-title">Сведения</div>
                                                <div className="category-details-meta">
                                                    <div>
                                                        <span>ID</span>
                                                        <strong>{selectedCategoryNode.id}</strong>
                                                    </div>
                                                    <div>
                                                        <span>Родитель</span>
                                                        <strong>{selectedCategoryNode.parentId ?? 'Нет'}</strong>
                                                    </div>
                                                    <div>
                                                        <span>Подкатегорий</span>
                                                        <strong>{selectedCategoryNode.children.length}</strong>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="category-inspector-section">
                                                <div className="category-inspector-section-title">Управление</div>
                                                <div className="category-details-actions">
                                                    <button
                                                        className="btn-secondary-custom category-action-button"
                                                        onClick={handleAddChildCategory}
                                                        disabled={!canEdit || savingCategoryDetails}
                                                        type="button"
                                                    >
                                                        Добавить подкатегорию
                                                    </button>
                                                    <button
                                                        className="btn-secondary-custom category-action-button"
                                                        onClick={handleOpenMoveCategory}
                                                        disabled={!canEdit || selectedCategoryNode.id === rootId || savingCategoryDetails}
                                                        type="button"
                                                    >
                                                        Переместить
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="category-inspector-section category-danger-section">
                                                <div className="category-inspector-section-title">Опасная зона</div>
                                                <button
                                                    className="category-delete-button"
                                                    onClick={handleDeleteCategory}
                                                    disabled={!canEdit || selectedCategoryNode.id === rootId || savingCategoryDetails}
                                                    type="button"
                                                >
                                                    Удалить категорию
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
        {movingCategory ? (
            <div className="modal-backdrop-custom" onClick={handleCloseMoveCategory}>
                <div className="modal-dialog-custom category-move-dialog animate-in" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-content-custom">
                        <div className="modal-header-custom">
                            <div>
                                <h3 className="modal-title-custom">Переместить категорию</h3>
                                <p className="modal-subtitle-custom">{movingCategory.name}</p>
                            </div>
                            <button className="modal-close-custom" onClick={handleCloseMoveCategory} disabled={savingMove}>
                                ✕
                            </button>
                        </div>
                        <div className="modal-body-custom">
                            <div className="category-move-tree">
                                <TreeControl
                                    data={movingParentTree}
                                    selectedNodeId={targetParentId}
                                    treeId={`category-move-${movingCategory.id}`}
                                    allowExpansion={true}
                                    onNodeSelected={(node) => setTargetParentId(node?.id)}
                                    showChildCount={false}
                                />
                            </div>
                        </div>
                        <div className="modal-footer-custom">
                            <button
                                className="btn-secondary-custom"
                                onClick={handleCloseMoveCategory}
                                disabled={savingMove}
                                type="button"
                            >
                                Отмена
                            </button>
                            <button
                                className="btn-primary-custom"
                                onClick={handleSaveMoveCategory}
                                disabled={savingMove || !targetParentId || targetParentId === movingCategory.parentId}
                                type="button"
                            >
                                {savingMove ? 'Перенос...' : 'Переместить'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        ) : null}
        {clearPreview ? (
            <div className="modal-backdrop-custom" onClick={handleCancelClearPreview}>
                <div className="modal-dialog-custom animate-in" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-content-custom">
                        <div className="modal-header-custom">
                            <div>
                                <h3 className="modal-title-custom">Очистка категории перед созданием подкатегории</h3>
                                <p className="modal-subtitle-custom">
                                    Чтобы создать подкатегорию, у родителя будут удалены привязки прайсов и отвязана номенклатура.
                                </p>
                            </div>
                            <button className="modal-close-custom" onClick={handleCancelClearPreview} disabled={clearSubmitting}>
                                ✕
                            </button>
                        </div>
                        <div className="modal-body-custom">
                            <p className="clear-preview-summary">
                                Будет отвязано НЛ: <strong>{clearPreview.productCount}</strong>
                            </p>
                            {clearPreview.mappings.length > 0 ? (
                                <>
                                    <div className="clear-preview-list-title">
                                        Будут удалены привязки прайсов ({clearPreview.mappings.length}):
                                    </div>
                                    <ul className="clear-preview-list">
                                        {clearPreview.mappings.map((m: SubcategoryClearMapping, i: number) => (
                                            <li key={m.supplierCategoryId ?? i}>
                                                {m.integrationName ? `${m.integrationName}: ` : ''}
                                                {m.fullPath || m.supplierCategoryName || `#${m.supplierCategoryId}`}
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            ) : (
                                <div className="clear-preview-list-title">Привязок прайсов нет.</div>
                            )}
                        </div>
                        <div className="modal-footer-custom">
                            <button
                                className="btn-primary-custom"
                                onClick={handleCancelClearPreview}
                                disabled={clearSubmitting}
                                type="button"
                                autoFocus
                            >
                                Отмена
                            </button>
                            <button
                                className="category-delete-button"
                                onClick={handleConfirmClearAndCreate}
                                disabled={clearSubmitting}
                                type="button"
                            >
                                {clearSubmitting ? 'Очистка...' : 'Очистить и создать'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        ) : null}
        </>
    );
};

export default CategoryManagement;
